import { Box } from '@chakra-ui/react';
import { Navigate } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import { LoaderOverlay } from 'components/LoaderOverlay/LoaderOverlay';
import { Header } from './Header/Header';
import { ModalReservationExpired } from './ModalReservationExpired/ModalReservationExpired';
import { ModalLogin } from 'components/ModalLogin/ModalLogin';
import { ModalSmartphoneInstructions } from './ModalSmartphoneInstructions/ModalSmartphoneInstructions';
import { ModalOpenQiCardInstructions } from './ModalOpenQiCardInstructions/ModalOpenQiCardInstructions';
import { useHoldTokenSessionTimerContext } from 'context/HoldTokenSessionTimerContext/HoldTokenSessionTimerContext';
import { useCartContext } from 'context/CartContext/CartContext';
import { ConcertResponse, MatchResponse } from 'api/responseTypes';
import useHylideBridge from 'hooks/useHylideBridge';
import { useCheckoutTracking } from './hooks/useCheckoutTracking';
import { usePriceCalculation } from './hooks/usePriceCalculation';
import { useCheckoutModals } from './hooks/useCheckoutModals';
import { useCheckoutSubmission } from './hooks/useCheckoutSubmission';
import { CheckoutContent } from './components/CheckoutContent';
import { useCheckoutMutation } from './hooks/useCheckoutMutation';
import { getEventInfo } from 'api/eventpage';
import { concertCacheKey, eventCacheKey } from 'api/eventpage/cacheKeys';

export const CheckoutPage = () => {
  useCheckoutTracking();

  const queryClient = useQueryClient();
  const { eventKey, invitationId } = useCartContext();

  const cachedEventData =
    queryClient.getQueryData<MatchResponse | ConcertResponse>(
      eventCacheKey(eventKey!, invitationId),
    ) ??
    queryClient.getQueryData<MatchResponse | ConcertResponse>(
      concertCacheKey(eventKey!, invitationId),
    );
  const hasCachedEventData = Boolean(cachedEventData);

  const {
    data: fetchedEventData,
    isFetching: isFetchingEvent,
    isError: isEventFetchError,
  } = useQuery(
    eventCacheKey(eventKey!, invitationId),
    () => getEventInfo(eventKey!, invitationId),
    {
      enabled: Boolean(eventKey) && !hasCachedEventData,
      cacheTime: 1000 * 60 * 30,
    },
  );

  const isMatchEvent = (d: MatchResponse | ConcertResponse | undefined): d is MatchResponse =>
    Boolean(
      d &&
        typeof (d as MatchResponse).teamA === 'object' &&
        (d as MatchResponse).teamA != null &&
        typeof (d as MatchResponse).teamB === 'object' &&
        (d as MatchResponse).teamB != null,
    );
  const isConcertEvent = (d: MatchResponse | ConcertResponse | undefined): d is ConcertResponse =>
    Boolean(d && 'imageLink' in d);

  const eventData = cachedEventData ?? fetchedEventData;
  const matchData = eventData && isMatchEvent(eventData) ? eventData : undefined;
  const concertData = eventData && isConcertEvent(eventData) ? eventData : undefined;

  const { holdTokenSessionTimer, hasTimerBeenSet } = useHoldTokenSessionTimerContext();
  const { status } = useCheckoutMutation();
  const hylideBridge = useHylideBridge();
  const {
    isAuthModalOpen,
    isSmartphoneInstructionsModalOpen,
    isQiCardInstructionsModalOpen,
    handleOpenSmartphoneModal,
    handleOpenQiCardInstructionsModal,
    handleCloseModal,
    handleCloseAuthModal,
    handleOpenAuthModal,
  } = useCheckoutModals();
  const { priceCalculation, isCalculatingPrice, handlePriceCalculated, handlePromoCodeRemoved } =
    usePriceCalculation({ onAuthRequired: handleOpenAuthModal });
  const requireAuthentication =
    matchData?.requireAuthentication ?? concertData?.requireAuthentication ?? false;
  const { handleSubmit, isLoading } = useCheckoutSubmission(
    priceCalculation,
    handleOpenAuthModal,
    requireAuthentication,
  );

  if (!hylideBridge) {
    return null;
  }

  if (eventKey && isEventFetchError && !hasCachedEventData) {
    return <Navigate to='/' replace={true} />;
  }

  return (
    // White page, scoped here rather than in the theme: `body` is `BG100` app-wide, and only
    // checkout is meant to be white (same approach as `AboutUsPage`). `minH='100vh'` so the white
    // reaches the footer on a short cart instead of stopping where the content ends.
    // `pb` rather than a margin on the content: a bottom *margin* collapses out through this
    // wrapper, which left a 40px band of body `BG100` between the content and the footer.
    //
    // The whole page opts into the Thmanyah brand face here, so descendants inherit it instead of
    // each component repeating the stack (the duplication TZ-CO-19 flagged).
    //
    // No `sx` heading override, unlike `AboutUsPage`: Chakra `Heading` pins `fontFamily` in the
    // theme's `baseStyle` and would ignore inheritance, but this page renders no headings at all —
    // no `<Heading>`, no `h1`-`h6`. Add one here if that changes.
    <Box bg='white' minH='100vh' pb='40px' fontFamily='thmanyah'>
      {(status === 'loading' || status === 'success' || isFetchingEvent) && <LoaderOverlay />}
      <Header />
      <CheckoutContent
        matchData={matchData}
        concertData={concertData}
        priceCalculation={priceCalculation}
        isCalculatingPrice={isCalculatingPrice}
        isLoading={isLoading}
        isFreeEvent={matchData?.isFreeEvent ?? concertData?.isFreeEvent}
        onSubmit={handleSubmit}
        onPriceCalculated={handlePriceCalculated}
        onPromoCodeRemoved={handlePromoCodeRemoved}
        onAuthRequired={handleOpenAuthModal}
        onOpenQiCardInstructionsModal={handleOpenQiCardInstructionsModal}
        onOpenSmartphoneModal={handleOpenSmartphoneModal}
      />
      <ModalReservationExpired isOpen={hasTimerBeenSet && holdTokenSessionTimer === 0} />
      <ModalLogin isOpen={isAuthModalOpen} onClose={handleCloseAuthModal} />
      <ModalSmartphoneInstructions
        isOpen={isSmartphoneInstructionsModalOpen}
        onClose={handleCloseModal}
      />
      <ModalOpenQiCardInstructions
        isOpen={isQiCardInstructionsModalOpen}
        onClose={handleCloseModal}
      />
    </Box>
  );
};
